import { useEffect, useRef, useState } from 'react';
import './RevealPair.css';

/* Card 8Wkq... (founder email 2026-10-27): the reveal presentation was written
   twice — once in Reveal history (side-by-side, clamped, labelled columns) and
   once in the thread's reveal card (stacked answers). The founder asked for the
   attached/initial reveal to use the same implementation as the full reveal, so
   both now render THIS component. One source of truth, one set of rules. */

export interface PairAnswer {
  memberId: string;
  text: string;
  /** Card LdgUHoQ4R4lS — this answer arrived after the reveal fired. */
  late?: boolean;
}

interface RevealPairProps {
  answers: PairAnswer[];
  /** Column order, normally the couple's member order so both cards read the
   *  same way down the history. Answers from an unknown member (left the pair)
   *  are appended after the known columns rather than dropped. */
  order: string[];
  /** memberId -> the name to show. Callers pass display names; the thread
   *  passes its own "You"/partner labels so the card keeps first person. */
  nameFor: (memberId: string) => string;
  /** Copy for a column whose author did not answer in time. */
  missingText?: string;
  /** Prefix for the data-* hooks probes anchor to. */
  testId?: string;
}

/**
 * One answer, clamped to six lines with an explicit expand. The toggle only
 * appears when the text actually overflows — measured, not guessed from
 * length, so a short answer never grows a dead control.
 */
function AnswerText({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open) return;
    setOverflows(el.scrollHeight - el.clientHeight > 1);
  }, [text, open]);

  return (
    <>
      <p ref={ref} className={`rp-text${open ? ' rp-text-open' : ''}`}>
        {text}
      </p>
      {(overflows || open) && (
        <button
          type="button"
          className="rp-expand"
          aria-expanded={open}
          onClick={() => setOpen(v => !v)}
        >
          {open ? 'Show less' : 'Read more'}
        </button>
      )}
    </>
  );
}

/**
 * Two answers, side by side on a wide surface and stacked below 640px with the
 * label repeated per answer, so no answer ever reads as a naked column.
 */
export function RevealPair({ answers, order, nameFor, missingText = 'No answer before the reveal.', testId }: RevealPairProps) {
  const extra = answers.map(a => a.memberId).filter(id => !order.includes(id));
  const columns = [...order, ...extra].map(memberId => ({
    memberId,
    answer: answers.find(a => a.memberId === memberId) ?? null,
  }));

  return (
    <div className="rp-pair" data-reveal-pair={testId ?? ''}>
      {columns.map(col => (
        <div className="rp-col" key={col.memberId} data-member={col.memberId}>
          <span className="rp-who">
            {nameFor(col.memberId)}
            {/* Card LdgUHoQ4R4lS — the record is honest about which answer
                missed the reveal window. */}
            {col.answer?.late && <span className="rp-late">Answered late</span>}
          </span>
          {col.answer ? (
            <AnswerText text={col.answer.text} />
          ) : (
            <p className="rp-missing">{missingText}</p>
          )}
        </div>
      ))}
    </div>
  );
}
